import React, { useState, useEffect } from "react";
import { useTabContext } from "./context/context";
import Availability from "./tabs/Availability";
import ListingDetails from "./tabs/ListingDetails";
import Users from "./tabs/Users";
import Premium from "./tabs/Premium";
import { FormProvider, useForm } from "react-hook-form";
import { PermissionsProvider } from "./data/Permissions";
import { handleSubmit } from "./data/submitHandler";
import { htmlToText } from "html-to-text";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import UploadError from "./components/UploadError";

const App = () => {
  const { loading, fetchAndSetData, provID, userRole, setCheckChange, setChangesMade, saving, setSaving, providerInfo, premiumInfo, inquiryEmail, provDescription, availabilityList, amenitiesList, amenitiesChecked, commonAreasList, commonAreasChecked, setUserList, changesMade, deletedImageIds, provRole, allServices, allowedServices, setServiceChange, serviceChange, setServicePending, notAccepting, setNotAccepting, notAcceptReason, setNotAcceptReason, errorMessage, warningMessage, setUploadError, uploadError, premiumChangesMade } = useTabContext();
  const methods = useForm();
  const {
    formState: { dirtyFields }
  } = methods;
  const [currentTab, setCurrentTab] = useState("tab1");

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchAndSetData(provID);
  }, [provID]);

  useEffect(() => {
    if (providerInfo) {
      methods.setValue("provider_id", providerInfo.ID);
      methods.setValue("org_name", providerInfo.name);
      methods.setValue("google_address", providerInfo.street_address);
      methods.setValue("city", providerInfo.city);
      methods.setValue("county", providerInfo.county);
      methods.setValue("state", providerInfo.state);
      methods.setValue("zip", providerInfo.zip);
      methods.setValue("phone", providerInfo.phone);
      methods.setValue("fax", providerInfo.fax_number);
      methods.setValue("inquiry_email", inquiryEmail);
      methods.setValue("not_accepting", notAccepting);
      methods.setValue("not_accepting_reason", notAcceptReason);
    }
  }, [providerInfo, inquiryEmail, notAccepting, notAcceptReason]);

  useEffect(() => {
    if (premiumInfo) {
      methods.setValue("premium.company_description", provDescription || "");
      methods.setValue("premium.company_website", premiumInfo.url || "");
      methods.setValue("premium.company_facebook", premiumInfo.comp_facebook || "");
      methods.setValue("premium.company_twitter", premiumInfo.comp_twitter || "");
      methods.setValue("premium.company_instagram", premiumInfo.comp_ig || "");
      methods.setValue("premium.company_video", premiumInfo.youtube || "");
      methods.setValue("premium.company_tour", premiumInfo.tour || "");
    }
  }, [premiumInfo, provDescription]);

  useEffect(() => {
    if (amenitiesList.length) {
      amenitiesList.forEach(amenity => {
        const amenityId = parseInt(amenity.id);
        methods.setValue(`amenities.${amenityId}`, amenitiesChecked.includes(amenityId));
      });
    }
    if (commonAreasList.length) {
      commonAreasList.forEach(commonArea => {
        const commonAreaId = parseInt(commonArea.id);
        methods.setValue(`common_areas.${commonAreaId}`, commonAreasChecked.includes(commonAreaId));
      });
    }
    if (allServices) {
      allServices.forEach(service => {
        const serviceId = parseInt(service.term_id, 10);
        const isAllowed = allowedServices.some(allowedService => allowedService.term_id === serviceId);
        methods.setValue(`services.${serviceId}`, isAllowed);
      });
    }
  }, [amenitiesList, commonAreasList, amenitiesChecked, commonAreasChecked]);

  const tabs = {
    tab1: "Availability",
    tab2: "Listing",
    tab3: "Users",
    tab4: "Premium"
  };

  const getVisibleTabs = () => {
    switch (provRole) {
      case "manager":
        return tabs;
      case "editor":
        return {
          tab1: tabs.tab1,
          tab2: tabs.tab2,
          tab4: tabs.tab4
        };
      case "reporter":
        return {
          tab1: tabs.tab1
        };
      default:
        return {};
    }
  };

  const visibleTabs = getVisibleTabs();

  const renderContent = () => {
    switch (currentTab) {
      case "tab1":
        return <Availability />;
      case "tab2":
        return <ListingDetails />;
      case "tab3":
        return <Users />;
      case "tab4":
        return <Premium />;
      default:
        return <div>Select a tab</div>;
    }
  };

  const onSubmit = async data => {
    const existingGalleryImages = (premiumInfo && premiumInfo.gallery) || [];
    if (serviceChange) {
      setServicePending(true);
    }
    handleSubmit(data, dirtyFields, methods, provID, fetchAndSetData, setSaving, setCheckChange, setChangesMade, changesMade, setUserList, existingGalleryImages, deletedImageIds, setUploadProgress, setIsUploading, methods.reset, methods.getValues, setUploadError, premiumChangesMade);
  };

  return (
    <PermissionsProvider initialRole={userRole}>
      <FormProvider {...methods}>
        <form className="relative" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="w-full flex justify-center">
            <button type="submit" className="ca-v3-admin-btn my-2 mx-auto" disabled={saving || Boolean(errorMessage)}>
              {saving ? "Saving..." : "Save / Update"}
            </button>
          </div>

          <UploadError />

          <div className="flex border-b-2 border-primaryBlue">
            {Object.entries(tabs).map(([key, label]) => (
              <button key={key} type="button" onClick={() => setCurrentTab(key)} className={`${currentTab === key ? "active-tab" : ""} general-prov-tab`}>
                {label}
              </button>
            ))}
          </div>

          <div className="tab-content">{loading ? <p>Loading...</p> : renderContent()}</div>

          <UploadError />

          <div className="w-full flex justify-center">
            <button type="submit" className="ca-v3-admin-btn my-2 mx-auto" disabled={saving || Boolean(errorMessage)}>
              {saving ? "Saving..." : "Save / Update"}
            </button>
          </div>
        </form>

        {/* 
          MUI Backdrop with CircularProgress 
          We can keep your existing spinner or replace it with a LinearProgress 
        */}
        <Backdrop sx={{ color: "#fff", zIndex: theme => theme.zIndex.drawer + 1 }} open={saving || isUploading}>
          {isUploading ? (
            <div className="w-full md:w-1/4 mx-auto">
              <Box sx={{ width: "100%", margin: "0 auto" }}>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 10, borderRadius: 5 }} />
                <p style={{ textAlign: "center", marginTop: 8 }}>{uploadProgress < 100 ? `${uploadProgress}% uploaded` : "Finishing Upload..."}</p>
              </Box>
            </div>
          ) : (
            <CircularProgress color="inherit" />
          )}
        </Backdrop>
      </FormProvider>
    </PermissionsProvider>
  );
};

export default App;
